import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SchemaOrg from '@/components/base/SchemaOrg';
import { articles } from '@/mocks/articles';
import { mainSiteUrl } from '@/mocks/categories';

const siteUrl = import.meta.env.VITE_SITE_URL;

const articleImageMap: Record<string, string> = {
  'itaobuyspreadsheet-complete-guide': 'https://readdy.ai/api/search-image?query=Modern%20organized%20desk%20workspace%20with%20dual%20monitors%20showing%20spreadsheet%20data%20analytics%20fashion%20product%20samples%20neatly%20arranged%20on%20light%20oak%20surface%20bright%20natural%20window%20lighting%20green%20plant%20accents%20clean%20productive%20editorial%20photography&width=1400&height=800&seq=article-hero-v4-01&orientation=landscape',
  'itaobuyspreadsheet-vs-pandabuy': 'https://readdy.ai/api/search-image?query=Two%20contrasting%20shopping%20experiences%20side%20by%20side%20one%20showing%20messy%20chaotic%20browsing%20the%20other%20showing%20organized%20spreadsheet%20data%20on%20white%20backdrop%20visual%20comparison%20concept%20editorial%20illustration%20style%20clean%20modern%20aesthetic&width=1400&height=800&seq=article-hero-v4-02&orientation=landscape',
  'how-to-avoid-scams-taobao-1688': 'https://readdy.ai/api/search-image?query=Magnifying%20glass%20over%20product%20listings%20with%20security%20shield%20overlay%20on%20clean%20white%20desk%20safety%20verification%20concept%20editorial%20photography%20bright%20daylight%20trusted%20and%20protected%20atmosphere%20professional%20corporate%20style&width=1400&height=800&seq=article-hero-v4-03&orientation=landscape',
  'best-dropshipping-spreadsheets-china': 'https://readdy.ai/api/search-image?query=Multiple%20product%20boxes%20and%20shipping%20packages%20on%20world%20map%20backdrop%20with%20spreadsheet%20data%20overlay%20global%20dropshipping%20logistics%20concept%20editorial%20photography%20clean%20commercial%20aesthetic%20soft%20blue%20gray%20tones&width=1400&height=800&seq=article-hero-v4-04&orientation=landscape',
  'itaobuyspreadsheet-pricing-strategy': 'https://readdy.ai/api/search-image?query=Calculator%20financial%20charts%20and%20upward%20trending%20graph%20with%20product%20price%20tags%20scattered%20on%20white%20marble%20desk%20profit%20optimization%20concept%20editorial%20business%20photography%20warm%20natural%20lighting%20clean%20professional%20aesthetic&width=1400&height=800&seq=article-hero-v4-05&orientation=landscape',
  'taobao-import-guide-for-resellers': 'https://readdy.ai/api/search-image?query=Cargo%20container%20ship%20at%20port%20with%20airplane%20in%20sky%20and%20product%20boxes%20in%20foreground%20international%20shipping%20import%20concept%20editorial%20photography%20bright%20daylight%20professional%20logistics%20aesthetic%20wide%20composition&width=1400&height=800&seq=article-hero-v4-06&orientation=landscape',
  'china-product-sourcing-checklist': 'https://readdy.ai/api/search-image?query=Detailed%20checklist%20notebook%20with%20pen%20and%20product%20samples%20spread%20across%20white%20desk%20organized%20planning%20concept%20editorial%20business%20photography%20bright%20natural%20light%20clean%20minimal%20aesthetic%20productive%20atmosphere&width=1400&height=800&seq=article-hero-v4-07&orientation=landscape',
  'itaobuyspreadsheet-for-amazon-sellers': 'https://readdy.ai/api/search-image?query=Amazon%20branded%20packaging%20with%20private%20label%20products%20and%20barcode%20scanner%20on%20modern%20warehouse%20shelf%20setup%20ecommerce%20fulfillment%20concept%20editorial%20product%20photography%20warm%20industrial%20lighting%20professional%20commercial%20aesthetic&width=1400&height=800&seq=article-hero-v4-08&orientation=landscape',
  'beginner-guide-china-wholesale': 'https://readdy.ai/api/search-image?query=Open%20map%20with%20compass%20and%20product%20catalog%20on%20warm%20wooden%20table%20beginner%20journey%20concept%20editorial%20photography%20soft%20golden%20morning%20light%20encouraging%20atmosphere%20clean%20composition%20travel%20and%20discovery%20mood&width=1400&height=800&seq=article-hero-v4-09&orientation=landscape',
  'itaobuyspreadsheet-shopping-guide': 'https://readdy.ai/api/search-image?query=Shopping%20cart%20filled%20with%20diverse%20fashion%20items%20on%20white%20studio%20backdrop%20with%20organized%20category%20tags%20floating%20around%20shopping%20guide%20concept%20editorial%20product%20photography%20clean%20professional%20aesthetic%20bright%20even%20lighting&width=1400&height=800&seq=article-hero-v4-10&orientation=landscape',
};

interface ArticleData {
  slug: string;
  title: string;
  excerpt: string;
  date: string;
  readTime: string;
  category: string;
  imageSeq: string;
  content: { type: string; text: string }[];
}

function generateKeywords(article: ArticleData): string {
  const baseKeywords = ['itaobuyspreadsheet', 'china sourcing', 'product sourcing spreadsheet', 'taobao', '1688'];
  const categoryKeywords: Record<string, string> = {
    'itaobuy sheet guide': 'sourcing guide, buying guide, verified suppliers',
    'Comparison': 'pandabuy comparison, sourcing platforms, buying agent alternative',
    'Safety Guide': 'scam prevention, safe sourcing, verified listings, supplier verification',
    'Dropshipping': 'dropshipping tools, china dropshipping, reseller spreadsheet',
    'Business Strategy': 'pricing strategy, profit margins, sourcing costs, ecommerce pricing',
    'Import Guide': 'taobao import, shipping guide, customs, international shipping',
    'Checklist': 'sourcing checklist, buyer checklist, quality control, supplier questions',
    'Amazon FBA': 'amazon sellers, private label, FBA sourcing, amazon products',
    'Beginner Guide': 'beginner sourcing, china wholesale, first order, new reseller',
    'Shopping Guide': 'category guide, shopping tips, best deals, product navigation',
  };
  const extra = categoryKeywords[article.category] || '';
  return [...baseKeywords, ...extra.split(', ').filter(Boolean)].join(', ');
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const articleCanonical = `${siteUrl}/article/${slug || ''}`;

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <title>Article Not Found | itaobuyspreadsheet</title>
        <meta name="robots" content="noindex, follow" />
        <Navbar />
        <main className="pt-24 pb-16 px-4 md:px-8 text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-950">Article Not Found</h1>
          <p className="mt-3 text-foreground-500 text-sm">
            The article you are looking for does not exist.{' '}
            <Link to="/" className="text-accent-600 hover:text-accent-700 underline underline-offset-2 font-medium cursor-pointer">
              Return to homepage
            </Link>
          </p>
        </main>
        <Footer />
      </div>
    );
  }

  const articleImage = articleImageMap[article.slug] || articleImageMap['itaobuyspreadsheet-complete-guide'];
  const keywords = generateKeywords(article);
  const pageTitle = `${article.title} | itaobuyspreadsheet`;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: article.title,
        description: article.excerpt,
        image: articleImage,
        datePublished: article.date,
        author: {
          '@type': 'Organization',
          name: 'itaobuyspreadsheet',
          url: siteUrl,
        },
        publisher: {
          '@type': 'Organization',
          name: 'itaobuyspreadsheet',
          logo: {
            '@type': 'ImageObject',
            url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/62f99c0415358bd5388d5ed1df9ac1ff.jpeg',
          },
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': articleCanonical,
        },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'itaobuy.my',
            item: siteUrl,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: article.category,
            item: `${siteUrl}/#categories`,
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: article.title,
          },
        ],
      },
    ],
  };

  return (
    <div className="min-h-screen bg-background-50">
      <title>{pageTitle}</title>
      <meta name="description" content={article.excerpt} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={articleCanonical} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={article.excerpt} />
      <meta property="og:url" content={articleCanonical} />
      <meta property="og:type" content="article" />
      <meta property="og:image" content={articleImage} />
      <meta property="og:image:width" content="1400" />
      <meta property="og:image:height" content="800" />
      <meta property="og:locale" content="en_US" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={article.excerpt} />
      <meta name="twitter:image" content={articleImage} />
      <SchemaOrg data={articleSchema} />
      <Navbar />

      <main>
        <article className="pt-24 md:pt-28 pb-16 md:pb-24">
          <header className="w-full px-4 md:px-8 mb-10 md:mb-14">
            <div className="max-w-3xl mx-auto">
              <div className="flex items-center gap-3 mb-4">
                <Link
                  to="/"
                  className="text-xs text-accent-600 hover:text-accent-700 font-medium cursor-pointer inline-flex items-center gap-1"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-left-line text-sm"></i>
                  </div>
                  Back to itaobuy.my
                </Link>
                <span className="text-foreground-300">/</span>
                <span className="text-xs text-foreground-500">{article.category}</span>
              </div>

              <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-950 leading-[1.12]">
                {article.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 mt-5 text-xs text-foreground-500">
                <span>{article.date}</span>
                <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                <span>{article.readTime}</span>
                <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                <span className="bg-accent-100 text-accent-700 px-2.5 py-1 rounded-full text-[11px] font-medium">
                  itaobuy sheet guide
                </span>
              </div>
            </div>
          </header>

          <div className="w-full px-4 md:px-8 mb-10">
            <div className="max-w-3xl mx-auto">
              <div className="rounded-2xl overflow-hidden h-56 md:h-80 lg:h-96">
                <img
                  src={articleImage}
                  alt={article.title}
                  title={article.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>

          <div className="w-full px-4 md:px-8">
            <div className="max-w-3xl mx-auto">
              <div className="prose-custom">
                {article.content.map((block, i) => {
                  if (block.type === 'h2') {
                    return (
                      <h2 key={i} className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mt-10 mb-4">
                        {block.text}
                      </h2>
                    );
                  }
                  return (
                    <p key={i} className="text-sm md:text-base text-foreground-600 leading-relaxed mb-5">
                      <span dangerouslySetInnerHTML={{ __html: block.text }} />
                    </p>
                  );
                })}
              </div>

              <div className="mt-12 pt-8 border-t border-background-200">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div>
                    <p className="text-xs text-foreground-400 mb-1">Explore more</p>
                    <Link
                      to="/"
                      className="text-sm font-semibold text-accent-600 hover:text-accent-700 cursor-pointer inline-flex items-center gap-1.5"
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-home-4-line text-sm"></i>
                      </div>
                      Return to itaobuy.my Homepage
                    </Link>
                  </div>

                  <a
                    href={mainSiteUrl}
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-file-excel-2-line text-base"></i>
                    </div>
                    itaobuy sheet
                  </a>
                </div>
              </div>

              <div className="mt-8 p-5 md:p-6 bg-accent-50/50 rounded-2xl border border-accent-100">
                <p className="text-sm font-semibold text-foreground-900 mb-2">Internal Links</p>
                <ul className="space-y-1.5">
                  <li>
                    <Link to="/" className="text-sm text-accent-600 hover:text-accent-700 cursor-pointer">
                      itaobuy.my — Sourcing Categories &amp; Tools
                    </Link>
                  </li>
                  <li>
                    <a href="/#categories" className="text-sm text-accent-600 hover:text-accent-700 cursor-pointer">
                      Browse All Product Categories
                    </a>
                  </li>
                  <li>
                    <a href="/#how-it-works" className="text-sm text-accent-600 hover:text-accent-700 cursor-pointer">
                      How itaobuyspreadsheet Works
                    </a>
                  </li>
                  <li>
                    <a href="/#faq" className="text-sm text-accent-600 hover:text-accent-700 cursor-pointer">
                      itaobuyspreadsheet FAQ
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}